import { Form, FormProps, Input } from 'antd';
import { isValidPhoneNumber } from 'libphonenumber-js';
import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { toast } from 'react-toastify';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { IntlTelData, IntlTelInput } from '@/components/shared/IntlTelInput';

import { selfPublishingLeads } from '@/services/SelfPublishingLeads';
import { ISelfPublishingLeads } from '@/utils/interfaces/SelfPublishingLeads';
type FieldType = {
  name: string;
  email: string;
  phone: string;
};

interface Props {
  setLoading: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  onSuccess: () => void;
}
const CallBackForm: FC<Props> = ({ loading, setLoading, onSuccess }) => {
  const [form] = Form.useForm();
  const [phone, setPhone] = useState<IntlTelData>();

  const onFinish: FormProps<FieldType>['onFinish'] = (values) => {
    // 1️⃣ Get captcha token before sending
    grecaptcha.enterprise.ready(async () => {
      try {
        const token = await grecaptcha.enterprise.execute(
          process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!, // from .env
          { action: 'lead_submit' },
        );

        // 2️⃣ Send form data + token to backend
        selfPublishingLeadsHandle({
          name: values.name,
          email: values.email,
          phone: values.phone,
          country_code: phone?.phonePrefix ? '+' + phone?.phonePrefix : '+91',
          recaptcha_token: token,
        });
      } catch (error) {
        console.error('Captcha Error:', error);
      }
    });
  };

  const onFinishFailed: FormProps<FieldType>['onFinishFailed'] = () =>
    // errorInfo,
    {};
  const selfPublishingLeadsHandle = async (data: ISelfPublishingLeads) => {
    try {
      setLoading(true);
      await toast.promise(selfPublishingLeads(data), {
        pending: 'Sending...',
        success: 'Sent Successfully',
      });
      onSuccess();
      form.resetFields();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form<FieldType>
      form={form}
      layout='vertical'
      initialValues={{ phone: '' }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Form.Item<FieldType>
        name='name'
        label='Name'
        className='capitalize'
        rules={[{ required: true, message: 'Please enter your name' }]}
      >
        <Input className='h-11' placeholder='Please enter your name' />
      </Form.Item>

      <Form.Item<FieldType>
        name='email'
        label='Email'
        className='capitalize'
        rules={[
          {
            required: true,
            type: 'email',
            message: 'Please enter a valid email',
          },
        ]}
      >
        <Input className='h-11' placeholder='Please enter your email' />
      </Form.Item>

      <Form.Item<ISelfPublishingLeads>
        name='phone'
        label='Phone Number'
        valuePropName='phone' // Tell Form where to read the value from props
        trigger='onChange' // Watch onChange event of the component
        rules={[
          { required: true, message: 'Please enter your phone number' },
          {
            validator: (_, value) => {
              const phoneStr = String(value || '').trim();
              if (!phoneStr) return Promise.reject('Phone number is required');
              return isValidPhoneNumber(phoneStr, 'IN')
                ? Promise.resolve()
                : Promise.reject('Invalid phone number');
            },
          },
        ]}
      >
        <IntlTelInput
          inputClassName='placeholder:text-[#595959]'
          phone={phone?.phone || ''}
          phoneCountryCode='in'
          onChange={(data) => {
            setPhone(data);
            form.setFieldsValue({ phone: data.phone });
          }}
        />
      </Form.Item>

      <Form.Item>
        <ButtonDefault
          loading={loading}
          disabled={loading}
          type='submit'
          htmlType='submit'
          variant={ButtonVariants.PRIMARY}
          size={5}
          className='w-full'
        >
          Request A Call Back
        </ButtonDefault>
      </Form.Item>
    </Form>
  );
};

export default CallBackForm;
